import dayjs, { Dayjs } from 'dayjs'
import { formatCrmKanbanBadgeDate } from '@pages/Admin/CRM/utils/formatData'
import {
  TaskTrackerBoardCard,
  TaskTrackerFilter,
  TaskTrackerFilterSelectOption,
  TaskTrackerTaskStickerValue,
} from '../store/taskTracker.types'
import type { BoardStickerRow } from '../store/domain.types'
import {
  isTaskTrackerDateLikeStickerFieldType,
  isTaskTrackerSystemDatetimeStickerFieldType,
} from './taskTrackerBoard.stickerFilters.utils'
import { sortTaskTrackerFilters } from './taskTrackerFilter.utils'
import { normalizeTaskDeadline } from './taskTrackerTaskInfo.utils'
import { formatTaskTrackerSystemDatetimeForDisplay } from './taskTrackerSystemDatetime.utils'

export const parseTaskTrackerStickerDateValue = (
  value: string | null,
): Dayjs | null => {
  if (!value) return null

  const isoDate = dayjs(value, 'YYYY-MM-DD', true)
  if (isoDate.isValid()) return isoDate

  const dottedDate = dayjs(value, 'DD.MM.YYYY', true)
  return dottedDate.isValid() ? dottedDate : null
}

export const formatTaskTrackerStickerDateForApi = (
  rawDateString: string | null | undefined,
): string | null => {
  if (!rawDateString) return null

  const parsed = dayjs(rawDateString, 'DD.MM.YYYY', true)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null
}

export const formatTaskTrackerStickerDateForDisplay = (value: string | null) => {
  if (!value?.trim()) return '—'
  return formatCrmKanbanBadgeDate(value)
}

export const formatTaskTrackerStickerDateTimeForDisplay = (
  value: string | null,
  platformTimeZone?: string | null,
) => formatTaskTrackerSystemDatetimeForDisplay(value, platformTimeZone)

export const sortTaskCardStickersByCatalogOrder = (
  stickers: TaskTrackerTaskStickerValue[],
  catalogFilters: TaskTrackerFilter[],
): TaskTrackerTaskStickerValue[] => {
  if (!catalogFilters.length) return stickers

  const positionByFieldSettingId = new Map(
    sortTaskTrackerFilters(catalogFilters).map((filter) => [
      filter.id,
      filter.position,
    ]),
  )

  return [...stickers].sort((left, right) => {
    const leftPosition =
      positionByFieldSettingId.get(left.fieldSettingId) ??
      Number.MAX_SAFE_INTEGER
    const rightPosition =
      positionByFieldSettingId.get(right.fieldSettingId) ??
      Number.MAX_SAFE_INTEGER

    return (
      leftPosition - rightPosition ||
      left.fieldSettingId - right.fieldSettingId
    )
  })
}

export const getTaskCardEditableDateStickers = (
  stickers: TaskTrackerTaskStickerValue[],
) =>
  stickers.filter(
    (sticker) =>
      sticker.isVisible &&
      isTaskTrackerDateLikeStickerFieldType(sticker.fieldType) &&
      !isTaskTrackerSystemDatetimeStickerFieldType(sticker.fieldType),
  )

export const isTaskTrackerInputStickerFieldType = (fieldType: string) =>
  fieldType === 'text' || fieldType === 'number'

export const getTaskCardEditableInputStickers = (
  stickers: TaskTrackerTaskStickerValue[],
) =>
  stickers.filter(
    (sticker) =>
      sticker.isVisible && isTaskTrackerInputStickerFieldType(sticker.fieldType),
  )

export const isTaskTrackerSelectStickerFieldType = (fieldType: string) =>
  fieldType === 'select'

export const getTaskCardEditableSelectStickers = (
  stickers: TaskTrackerTaskStickerValue[],
) =>
  stickers.filter(
    (sticker) =>
      sticker.isVisible &&
      isTaskTrackerSelectStickerFieldType(sticker.fieldType),
  )

export const buildTaskCardSelectStickerOptions = (
  catalogOptions: TaskTrackerFilterSelectOption[],
  currentValue: string | null,
) => {
  const options: Array<{
    value: string
    label: string
    title: string
    color?: string
  }> = catalogOptions.map((option) => ({
    value: option.label,
    label: option.label,
    title: option.label,
    color: option.color,
  }))

  const trimmedValue = currentValue?.trim()
  if (
    trimmedValue &&
    !options.some((option) => option.value === trimmedValue)
  ) {
    options.unshift({
      value: trimmedValue,
      label: trimmedValue,
      title: trimmedValue,
    })
  }

  return options
}

export const formatTaskTrackerInputStickerDisplayValue = (
  fieldType: string,
  value: string | null,
) => {
  const trimmed = value?.trim()
  if (!trimmed) return '—'
  return trimmed
}

export const getTaskCardStickerDisplayValue = (
  card: Pick<TaskTrackerBoardCard, 'deadline' | 'stickers'>,
  sticker: Pick<TaskTrackerTaskStickerValue, 'fieldType' | 'value'>,
): string | null => {
  if (sticker.fieldType === 'deadline') {
    const deadline = card.deadline?.trim()
    if (deadline) return deadline
  }

  const value = sticker.value?.trim()
  return value || null
}

export const hasTaskCardStickerDisplayValue = (
  card: Pick<TaskTrackerBoardCard, 'deadline' | 'stickers'>,
  sticker: TaskTrackerTaskStickerValue,
) => Boolean(getTaskCardStickerDisplayValue(card, sticker))

export const isTaskCardAssignableSticker = (
  sticker: TaskTrackerTaskStickerValue,
) =>
  sticker.isVisible &&
  sticker.fieldType !== 'performer' &&
  !isTaskTrackerSystemDatetimeStickerFieldType(sticker.fieldType)

export const isTaskCardPerformerStickerVisible = (
  boardStickers: BoardStickerRow[],
) =>
  boardStickers.some(
    (boardSticker) =>
      boardSticker.fieldType === 'performer' && boardSticker.isVisible,
  )

export const getVisibleBoardStickerFieldSettingIds = (
  boardStickers: BoardStickerRow[],
): Set<number> =>
  new Set(
    boardStickers
      .filter((boardSticker) => boardSticker.isVisible)
      .map((boardSticker) => boardSticker.fieldSettingId),
  )

export const isTaskCardStickerVisibleOnBoard = (
  fieldSettingId: number,
  visibleBoardStickerFieldSettingIds: Set<number>,
) => visibleBoardStickerFieldSettingIds.has(fieldSettingId)

export const getTaskCardEmptyVisibleStickers = (
  card: Pick<TaskTrackerBoardCard, 'deadline' | 'stickers'>,
  stickers: TaskTrackerTaskStickerValue[],
) =>
  stickers.filter(
    (sticker) =>
      isTaskCardAssignableSticker(sticker) &&
      !hasTaskCardStickerDisplayValue(card, sticker),
  )

export const isTaskCardInlineEditableStickerFieldType = (fieldType: string) =>
  !isTaskTrackerSystemDatetimeStickerFieldType(fieldType) &&
  (isTaskTrackerDateLikeStickerFieldType(fieldType) ||
    isTaskTrackerInputStickerFieldType(fieldType) ||
    isTaskTrackerSelectStickerFieldType(fieldType))

export const getVisibleTaskCardStickersInCatalogOrder = (
  card: Pick<TaskTrackerBoardCard, 'deadline' | 'stickers'>,
  orderedStickers: TaskTrackerTaskStickerValue[],
  catalogFilters: TaskTrackerFilter[],
  options?: {
    isStickerVisibleOnCard?: (sticker: TaskTrackerTaskStickerValue) => boolean
    visibleBoardStickerFieldSettingIds?: Set<number>
  },
): TaskTrackerTaskStickerValue[] => {
  const visibleBoardStickerFieldSettingIds =
    options?.visibleBoardStickerFieldSettingIds
  const isBoardStickerVisible = (fieldSettingId: number) =>
    visibleBoardStickerFieldSettingIds == null ||
    visibleBoardStickerFieldSettingIds.has(fieldSettingId)

  const isStickerVisibleOnCard =
    options?.isStickerVisibleOnCard ??
    ((sticker: TaskTrackerTaskStickerValue) =>
      hasTaskCardStickerDisplayValue(card, sticker))

  const inlineFieldSettingIds = new Set(
    orderedStickers
      .filter((sticker) => isTaskCardInlineEditableStickerFieldType(sticker.fieldType))
      .map((sticker) => sticker.fieldSettingId),
  )

  const stickerByFieldSettingId = new Map<number, TaskTrackerTaskStickerValue>()
  orderedStickers.forEach((sticker) => {
    stickerByFieldSettingId.set(sticker.fieldSettingId, sticker)
  })

  card.stickers.forEach((sticker) => {
    if (
      stickerByFieldSettingId.has(sticker.fieldSettingId) ||
      inlineFieldSettingIds.has(sticker.fieldSettingId)
    ) {
      return
    }

    if (
      hasTaskCardStickerDisplayValue(card, sticker) &&
      isBoardStickerVisible(sticker.fieldSettingId)
    ) {
      stickerByFieldSettingId.set(sticker.fieldSettingId, sticker)
    }
  })

  return sortTaskCardStickersByCatalogOrder(
    [...stickerByFieldSettingId.values()],
    catalogFilters,
  ).filter(
    (sticker) =>
      isBoardStickerVisible(sticker.fieldSettingId) &&
      isStickerVisibleOnCard(sticker),
  )
}

export const mergeBoardStickersWithTaskCard = (
  boardStickers: BoardStickerRow[],
  card: Pick<TaskTrackerBoardCard, 'deadline' | 'stickers'>,
): TaskTrackerTaskStickerValue[] => {
  const taskStickerByFieldSettingId = new Map(
    card.stickers.map((sticker) => [sticker.fieldSettingId, sticker]),
  )

  return boardStickers
    .filter((boardSticker) => boardSticker.isVisible)
    .map((boardSticker) => {
      const taskSticker = taskStickerByFieldSettingId.get(
        boardSticker.fieldSettingId,
      )

      if (taskSticker) {
        return {
          ...taskSticker,
          fieldType: taskSticker.fieldType || boardSticker.fieldType,
          title: taskSticker.title || boardSticker.title,
          color: taskSticker.color || boardSticker.color,
          isVisible: boardSticker.isVisible,
          systemKey: taskSticker.systemKey ?? boardSticker.systemKey,
          isSystem: taskSticker.isSystem ?? boardSticker.isSystem,
        }
      }

      const value =
        boardSticker.fieldType === 'deadline'
          ? normalizeTaskDeadline(card.deadline)
          : null

      return {
        fieldSettingId: boardSticker.fieldSettingId,
        fieldType: boardSticker.fieldType,
        title: boardSticker.title,
        color: boardSticker.color,
        isVisible: boardSticker.isVisible,
        value,
        systemKey: boardSticker.systemKey,
        isSystem: boardSticker.isSystem,
      }
    })
}

export const getTaskCardStickerBadgeStyle = (color: string) => {
  const accent = color?.trim() || '#9747ff'

  return {
    color: accent,
    borderColor: `${accent}33`,
    backgroundColor: `${accent}17`,
  } as const
}

export const getTaskCardSelectStickerBadgeStyle = (
  catalogOptions: TaskTrackerFilterSelectOption[],
  selectedValue: string | null | undefined,
  stickerColor: string,
) => {
  const trimmedValue = selectedValue?.trim()
  if (!trimmedValue) {
    return getTaskCardStickerBadgeStyle(stickerColor)
  }

  const matchedOption = catalogOptions.find(
    (option) => option.label.trim() === trimmedValue,
  )

  return getTaskCardStickerBadgeStyle(
    matchedOption?.color?.trim() || stickerColor,
  )
}

export const mergeTaskStickerValues = (
  previousStickers: TaskTrackerTaskStickerValue[],
  incomingStickers: TaskTrackerTaskStickerValue[],
): TaskTrackerTaskStickerValue[] => {
  if (!incomingStickers.length) {
    return previousStickers
  }

  const byId = new Map(
    previousStickers.map((sticker) => [sticker.fieldSettingId, sticker]),
  )

  incomingStickers.forEach((sticker) => {
    const previous = byId.get(sticker.fieldSettingId)
    byId.set(
      sticker.fieldSettingId,
      previous ? { ...previous, ...sticker } : sticker,
    )
  })

  return Array.from(byId.values())
}

export type ApplyTaskStickerCrmLeadMeta = {
  crmLeadId?: number | null
  crmFunnelId?: number | null
}

export const applyTaskStickerValue = (
  task: TaskTrackerBoardCard,
  fieldSettingId: number,
  value: string | null,
  meta?: Pick<
    TaskTrackerTaskStickerValue,
    'fieldType' | 'title' | 'color' | 'isVisible'
  >,
  crmLeadMeta?: ApplyTaskStickerCrmLeadMeta,
): TaskTrackerBoardCard => {
  const stickers = [...task.stickers]
  const index = stickers.findIndex(
    (sticker) => sticker.fieldSettingId === fieldSettingId,
  )

  const crmLeadPatch =
    crmLeadMeta != null
      ? {
          crmLeadId: crmLeadMeta.crmLeadId ?? null,
          crmFunnelId: crmLeadMeta.crmFunnelId ?? null,
        }
      : {}

  if (index >= 0) {
    stickers[index] = {
      ...stickers[index],
      value,
      ...crmLeadPatch,
    }
  } else if (meta) {
    stickers.push({
      fieldSettingId,
      fieldType: meta.fieldType,
      title: meta.title,
      color: meta.color,
      isVisible: meta.isVisible,
      value,
      ...crmLeadPatch,
    })
  }

  return {
    ...task,
    stickers,
  }
}

export const mergeTaskCardAfterStickerSave = (
  previousTask: TaskTrackerBoardCard,
  apiTask: TaskTrackerBoardCard,
  fieldSettingId: number,
  value: string | null,
  meta?: Pick<
    TaskTrackerTaskStickerValue,
    'fieldType' | 'title' | 'color' | 'isVisible'
  >,
  crmLeadMeta?: ApplyTaskStickerCrmLeadMeta,
): TaskTrackerBoardCard => {
  const apiSticker = apiTask.stickers.find(
    (sticker) => sticker.fieldSettingId === fieldSettingId,
  )
  // Клиентский crmLeadMeta (в т.ч. явный clear → null) важнее ответа API:
  // иначе устаревшие crmLeadId/crmFunnelId с бэка перетирают сброс.
  const resolvedCrmLeadMeta: ApplyTaskStickerCrmLeadMeta | undefined =
    crmLeadMeta != null
      ? {
          crmLeadId: crmLeadMeta.crmLeadId ?? null,
          crmFunnelId: crmLeadMeta.crmFunnelId ?? null,
        }
      : apiSticker?.crmLeadId != null || apiSticker?.crmFunnelId != null
        ? {
            crmLeadId: apiSticker.crmLeadId ?? null,
            crmFunnelId: apiSticker.crmFunnelId ?? null,
          }
        : undefined

  const merged = applyTaskStickerValue(
    {
      ...previousTask,
      stickers: mergeTaskStickerValues(previousTask.stickers, apiTask.stickers),
    },
    fieldSettingId,
    value,
    meta,
    resolvedCrmLeadMeta,
  )

  if (meta?.fieldType === 'deadline') {
    return {
      ...merged,
      deadline: value,
    }
  }

  return merged
}
